import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./ManagementReports.module.scss";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import axiosInstance from "../../utils/utils";
import {
  setConfluencePage,
  setConfluenceTree,
} from "../../redux/confluenceStore";
import { setLoadingContext } from "../../redux/loaderStore";
import {
  COMPONENT_LOADER,
  CONFLUENCE_TREE_LOADER,
  MODAL_LOADER,
} from "../../constants/loader";
import pageNotFoundIllustration from "../../icons/404.svg";
import { useLocation, useParams } from "react-router-dom";
import { hasRealContent } from "../../utils/helpers";
import { showModal } from "../../redux/modalStore";
import ConfluencePageDataModal from "../../modals/ConfluencePageDataModal";
import { setModalConfluencePage } from "../../redux/modalConfluenceStore";
import {
  showNotFoundPage,
  updateFloatingBarVisible,
} from "../../redux/layoutStore";
import ExpandableMenu from "../../components/ExpandableMenu/ExpandableMenu";
import Input from "../../components/Input/Input";
import Button from "../../components/Button/Button";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import {
  ExternalLink,
  FileBarChart,
  FileText,
  GripVertical,
  Info,
  RefreshCw,
  Search,
} from "lucide-react";

const ManagementReports = () => {
  const { orgId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const leftSideRef = useRef(null);
  const panelGroupRef = useRef(null);

  const fetchingTree = useRef(false);
  const fetchingPage = useRef(false);
  const hasFetchedPageData = useRef(false);

  const [selectedPageId, setSelectedPageId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [isResizingPanels, setIsResizingPanels] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    const saved = Number(localStorage.getItem("mr_left_panel_width"));
    return Number.isFinite(saved) && saved > 0 ? saved : 380;
  });
  const leftPanelWidthRef = useRef(leftPanelWidth);

  const confluenceTreeData = useSelector(
    (state) => state.confluence.confluenceTreeData,
  );
  const confluencePageData = useSelector(
    (state) => state.confluence.confluencePageData,
  );
  const modalConfluencePageData = useSelector(
    (state) => state.modalConfluence.modalConfluencePageData,
  );
  const module = useSelector((state) => state.user.modules[orgId]);
  const loadingContexts = useSelector((state) => state.loader.loadingContexts);
  const notFoundPage = useSelector((state) => state.layout.notFoundPage);
  const currentOrganization = useSelector(
    (state) => state.user.organizations[orgId],
  );
  const currentUser = useSelector((state) => state.user.user);

  const isComponentLoading = loadingContexts[COMPONENT_LOADER];
  const isTreeLoading = loadingContexts[CONFLUENCE_TREE_LOADER];
  const hasTree =
    Array.isArray(confluenceTreeData?.children) &&
    confluenceTreeData.children.length > 0;
  const isInitialTreeLoading = isTreeLoading && !hasTree;

  useEffect(() => {
    leftPanelWidthRef.current = leftPanelWidth;
  }, [leftPanelWidth]);

  const persistLeftPanelWidth = useCallback((width) => {
    try {
      localStorage.setItem("mr_left_panel_width", String(Math.round(width)));
    } catch {
      // ignore
    }
  }, []);

  const clampLeftPanelWidth = useCallback(
    (proposedWidth) => {
      const MIN_LEFT = 300;
      const MAX_LEFT = 560;
      const MIN_RIGHT = 520;
      const RESIZER_WIDTH = 24;

      const containerWidth =
        panelGroupRef.current?.getBoundingClientRect?.().width ?? null;

      const maxByContainer = containerWidth
        ? Math.max(MIN_LEFT, containerWidth - MIN_RIGHT - RESIZER_WIDTH)
        : MAX_LEFT;

      const max = Math.min(MAX_LEFT, maxByContainer);
      return Math.min(max, Math.max(MIN_LEFT, proposedWidth));
    },
    [],
  );

  useEffect(() => {
    setLeftPanelWidth((currentWidth) => clampLeftPanelWidth(currentWidth));
  }, [clampLeftPanelWidth]);

  useEffect(() => {
    const handleResize = () => {
      setLeftPanelWidth((currentWidth) => clampLeftPanelWidth(currentWidth));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [clampLeftPanelWidth]);

  useEffect(() => {
    if (!isResizingPanels) return undefined;

    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
    };
  }, [isResizingPanels]);

  const handleResizerPointerDown = useCallback(
    (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      event.preventDefault();

      const startX = event.clientX;
      const startWidth = leftPanelWidthRef.current;

      setIsResizingPanels(true);
      event.currentTarget?.setPointerCapture?.(event.pointerId);

      const handlePointerMove = (moveEvent) => {
        const nextWidth = clampLeftPanelWidth(
          startWidth + (moveEvent.clientX - startX),
        );
        leftPanelWidthRef.current = nextWidth;
        setLeftPanelWidth(nextWidth);
      };

      const handlePointerUp = () => {
        setIsResizingPanels(false);
        persistLeftPanelWidth(leftPanelWidthRef.current);
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    },
    [clampLeftPanelWidth, persistLeftPanelWidth],
  );

  const handleResizerKeyDown = useCallback(
    (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();

      const delta = event.key === "ArrowLeft" ? -24 : 24;
      setLeftPanelWidth((currentWidth) => {
        const nextWidth = clampLeftPanelWidth(currentWidth + delta);
        persistLeftPanelWidth(nextWidth);
        return nextWidth;
      });
    },
    [clampLeftPanelWidth, persistLeftPanelWidth],
  );

  useEffect(() => {
    dispatch(updateFloatingBarVisible(true));
  }, [dispatch]);

  const sessionToken = localStorage.getItem("session_token");

  const normalizeRole = (role) => role?.toLowerCase?.() || "";
  const canSync = useMemo(() => {
    const role = normalizeRole(currentUser?.Role);
    return role === "consultant" || role === "qmsadmin";
  }, [currentUser]);

  const modifiedPath = useMemo(() => {
    const segments = location.pathname.split("/");
    return `/${segments[2] || ""}`;
  }, [location.pathname]);

  const currentModule = useMemo(
    () => module?.find((mod) => mod.path === modifiedPath),
    [module, modifiedPath],
  );

  const resolvedSpaceKey =
    currentModule?.params?.space || confluenceTreeData?.space_key || "";

  const clearConfluenceData = useCallback(() => {
    dispatch(setConfluenceTree(null));
    dispatch(setConfluencePage(null));
  }, [dispatch]);

  const fetchManagementReportsTree = useCallback(async () => {
    if (!orgId || !sessionToken || fetchingTree.current) return;

    const item = module?.find(
      (data) => `/${orgId}${data.path}` === location.pathname,
    );

    if (!item || item.module !== "ConfluenceDocumentTree" || !item.params) {
      return;
    }

    fetchingTree.current = true;
    dispatch(
      setLoadingContext({ context: CONFLUENCE_TREE_LOADER, isLoading: true }),
    );

    const { space, page_name, page_id } = item.params;
    let queryParams = `org_id=${orgId}`;

    if (space) queryParams += `&space_key=${space}`;
    if (page_name) queryParams += `&page_name=${encodeURIComponent(page_name)}`;
    else if (page_id) queryParams += `&page_id=${page_id}`;

    const url = `/confluence/tree?${queryParams}`;

    try {
      const response = await axiosInstance.get(url, {
        headers: { "session-token": sessionToken },
      });
      dispatch(showNotFoundPage(false));
      dispatch(setConfluenceTree(response.data));
    } catch (error) {
      if (error.response && error.response.status === 404) {
        dispatch(showNotFoundPage(true));
      } else {
        dispatch(showModal({ name: "error_modal" }));
      }
    } finally {
      dispatch(
        setLoadingContext({ context: CONFLUENCE_TREE_LOADER, isLoading: false }),
      );
      fetchingTree.current = false;
    }
  }, [dispatch, location.pathname, module, orgId, sessionToken]);

  const getConfluencePageData = useCallback(
    async (pageId, pageName) => {
      if (!orgId || !pageId || fetchingPage.current) return;
      if (!sessionToken) {
        clearConfluenceData();
        return;
      }

      fetchingPage.current = true;
      setSelectedPageId(pageId);

      dispatch(setLoadingContext({ context: COMPONENT_LOADER, isLoading: true }));

      try {
        const response = await axiosInstance.get(
          `/confluence/page?org_id=${orgId}&space_key=${resolvedSpaceKey}&page_id=${pageId}`,
          { headers: { "session-token": sessionToken } },
        );
        dispatch(showNotFoundPage(false));
        dispatch(setConfluencePage(response.data));
        hasFetchedPageData.current = true;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          dispatch(showNotFoundPage(true));
        } else {
          dispatch(showModal({ name: "error_modal" }));
        }
      } finally {
        dispatch(
          setLoadingContext({ context: COMPONENT_LOADER, isLoading: false }),
        );
        fetchingPage.current = false;
      }
    },
    [clearConfluenceData, dispatch, orgId, resolvedSpaceKey, sessionToken],
  );

  const getModalConfluencePageData = useCallback(
    async (space, pageId) => {
      if (!orgId || !pageId || !sessionToken) return;

      dispatch(setLoadingContext({ context: MODAL_LOADER, isLoading: true }));
      try {
        const response = await axiosInstance.get(
          `/confluence/page?org_id=${orgId}&space_key=${space}&page_id=${pageId}`,
          { headers: { "session-token": sessionToken } },
        );
        dispatch(setModalConfluencePage(response.data));
      } catch {
        dispatch(showModal({ name: "error_modal" }));
      } finally {
        dispatch(setLoadingContext({ context: MODAL_LOADER, isLoading: false }));
      }
    },
    [dispatch, orgId, sessionToken],
  );

  const handleLinkClick = useCallback(
    (event, cSpace, cPageId) => {
      event.preventDefault();
      getModalConfluencePageData(cSpace, cPageId);
      dispatch(showModal({ name: "quality_management_system_modal" }));
    },
    [dispatch, getModalConfluencePageData],
  );

  useEffect(() => {
    window.handleLinkClick = handleLinkClick;
  }, [handleLinkClick]);

  const addOnClickHandlersToLinks = useCallback((htmlContent) => {
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(htmlContent || "", "text/html");

    const links = htmlDoc.querySelectorAll("a");
    const fileExtensions = /\.(pdf|docx|xlsx|csv|pptx|txt|zip)$/i;

    links.forEach((link) => {
      const cSpace = link.getAttribute("c-space");
      const cPageId = link.getAttribute("c-page_id");
      const href = link.getAttribute("href");

      if (href && fileExtensions.test(href)) {
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
      } else if (cSpace && cPageId) {
        link.setAttribute(
          "onclick",
          `handleLinkClick(event, '${cSpace}', ${cPageId})`,
        );
      } else if (href && href.startsWith("http")) {
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
      }
    });

    return htmlDoc.body.innerHTML;
  }, []);

  const findNodeById = useCallback((nodes, targetId) => {
    if (!Array.isArray(nodes) || targetId == null) return null;
    for (const node of nodes) {
      if (String(node?.page_id) === String(targetId)) return node;
      if (node?.children?.length > 0) {
        const found = findNodeById(node.children, targetId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const selectedTreeNode = useMemo(
    () => findNodeById(confluenceTreeData?.children, selectedPageId),
    [confluenceTreeData, findNodeById, selectedPageId],
  );

  const filterTreeData = useCallback((treeData, query) => {
    if (!query) return treeData;
    const normalizedQuery = query.toLowerCase();
    const result = [];

    const searchTree = (nodes) => {
      nodes.forEach((node) => {
        if (String(node?.page_name || "").toLowerCase().includes(normalizedQuery)) {
          result.push(node);
        }
        if (node?.children?.length > 0) searchTree(node.children);
      });
    };

    searchTree(treeData);
    return result;
  }, []);

  const filteredSearchResults = useMemo(
    () => filterTreeData(confluenceTreeData?.children || [], searchQuery),
    [confluenceTreeData, filterTreeData, searchQuery],
  );

  const isConfluencePageData = !_.isEmpty(confluencePageData);
  const isContentEmpty = !hasRealContent(confluencePageData?.body);

  const formatLastEdited = (dateValue) => {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const lastEdited =
    formatLastEdited(confluencePageData?.updated_at) ||
    formatLastEdited(confluencePageData?.updated);

  const updatedBy =
    confluencePageData?.updated_by ||
    confluencePageData?.updatedBy ||
    confluencePageData?.updated_by_email ||
    confluencePageData?.updated_by_user ||
    null;

  useEffect(() => {
    if (!Array.isArray(confluenceTreeData?.children)) return;
    if (confluenceTreeData.children.length === 0) return;
    if (hasFetchedPageData.current) return;

    const firstPage = confluenceTreeData.children.find((node) => node?.page_id);
    if (firstPage?.page_id) {
      getConfluencePageData(firstPage.page_id, firstPage.page_name);
    }
  }, [confluenceTreeData, getConfluencePageData]);

  useEffect(() => {
    clearConfluenceData();
    setSelectedPageId(null);
    hasFetchedPageData.current = false;
  }, [clearConfluenceData, orgId]);

  const handleEditReportClick = () => {
    const atlassianDomain = currentOrganization?.AtlassianDomain;
    const targetPageId = selectedPageId;
    if (atlassianDomain && resolvedSpaceKey && targetPageId) {
      const editUrl = `https://${atlassianDomain}/wiki/spaces/${resolvedSpaceKey}/pages/${targetPageId}`;
      window.open(editUrl, "_blank");
    }
  };

  const handleManualSync = async () => {
    if (!canSync || isTreeLoading) return;
    await fetchManagementReportsTree();
    setSelectedPageId(null);
    dispatch(setConfluencePage(null));
    hasFetchedPageData.current = false;
  };

  const PageStructure = ({ nodes, depth = 0 }) => {
    if (!Array.isArray(nodes) || nodes.length === 0) return null;
    return (
      <>
        {nodes.map((node) => {
          const hasChildren = Array.isArray(node.children) && node.children.length > 0;
          return (
            <div className={styles.structureNode} key={node.page_id || node.page_name}>
              <button
                type="button"
                className={styles.structureRow}
                style={{ paddingLeft: `${depth * 18 + 12}px` }}
                onClick={() => getConfluencePageData(node.page_id, node.page_name)}
              >
                <span className={styles.structureIcon} aria-hidden="true">
                  <FileText size={16} />
                </span>
                <span className={styles.structureText}>{node.page_name}</span>
              </button>
              {hasChildren && <PageStructure nodes={node.children} depth={depth + 1} />}
            </div>
          );
        })}
      </>
    );
  };

  const LoadingSkeleton = () => (
    <div className={styles.loadingCard}>
      <div className={styles.skeletonLine} style={{ width: "60%" }} />
      <div className={styles.skeletonLine} style={{ width: "92%" }} />
      <div className={styles.skeletonLine} style={{ width: "88%" }} />
      <div className={styles.skeletonLine} style={{ width: "78%" }} />
      <div className={styles.skeletonLine} style={{ width: "64%" }} />
    </div>
  );

  const PageLoadingSkeleton = () => (
    <div className={styles.rightPanelSkeleton}>
      <div
        className={styles.skeletonBlock}
        style={{ width: "72%", height: 52 }}
      />
      <div className={styles.skeletonBlock} style={{ width: "94%", height: 18 }} />
      <div className={styles.skeletonBlock} style={{ width: "90%", height: 18 }} />
      <div className={styles.skeletonBlock} style={{ width: "82%", height: 18 }} />
      <div className={styles.skeletonBlock} style={{ width: "68%", height: 18 }} />
    </div>
  );

  return (
    <MainLayout>
      <div className={styles.column}>
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h1 className={styles.headerTitle}>
              <span className={styles.headerTitleIcon} aria-hidden="true">
                <FileBarChart size={32} />
              </span>
              {t("modules.managementreports", "Management Reports")}
            </h1>
            <p className={styles.headerSubtitle}>
              {t(
                "managementReports.subtitle",
                "Access your Borderly+ Management Reports",
              )}
            </p>
          </div>

          <div className={styles.headerActions}>
            <div className={styles.availabilityAlert}>
              <span className={styles.availabilityIcon} aria-hidden="true">
                <Info size={16} />
              </span>
              <div className={styles.availabilityText}>
                <div className={styles.availabilityTitle}>
                  {t("managementReports.availabilityTitle", "Reports Available")}
                </div>
                <div className={styles.availabilityDescription}>
                  {t(
                    "managementReports.availabilityDescription",
                    "Auto-synced from Borderly+ Define",
                  )}
                </div>
              </div>
            </div>

            {canSync && (
              <Button
                text={t("managementReports.syncButton", "Re-sync")}
                className={styles.syncButton}
                icon={<RefreshCw size={16} />}
                iconPosition="left"
                onClick={handleManualSync}
                disabled={isTreeLoading || !sessionToken}
              />
            )}
          </div>
        </div>

        {notFoundPage ? (
          <div className={styles.pageNotFoundWrapper}>
            <img
              src={pageNotFoundIllustration}
              alt={t("twoColumnLayout.notFoundIllustrationAlt", "Not found illustration")}
            />
            <span>{t("qualityManagement.notFound.message")}</span>
          </div>
        ) : (
          <>
            {isInitialTreeLoading ? (
              <div className={styles.loadingPage}>
                <LoadingSkeleton />
              </div>
            ) : (
              <>
                <div className={styles.searchCard}>
                  <div className={styles.searchCardTitle}>
                    {t(
                      "managementReports.searchCardTitle",
                      "Search Borderly+ Management Reports",
                    )}
                  </div>
                  <div className={styles.searchRow}>
                    <Input
                      wrapperClassName={styles.searchInputWrapper}
                      className={styles.searchInput}
                      placeholder={t(
                        "managementReports.search.placeholder",
                        "Search for reports...",
                      )}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      isClearable
                      onClear={() => setSearchQuery("")}
                      icon={Search}
                      iconPosition="left"
                      iconStyle={{ width: 18, height: 18, color: "#5D5D6F" }}
                    />
                    <button
                      type="button"
                      className={styles.searchButton}
                      onClick={() =>
                        leftSideRef.current?.scrollTo?.({
                          top: 0,
                          behavior: "smooth",
                        })
                      }
                      disabled={!searchQuery}
                    >
                      {t("managementReports.searchButton", "Search")}
                    </button>
                  </div>
                </div>

                <div
                  className={styles.panelGroup}
                  ref={panelGroupRef}
                  style={{ "--mr-left-panel-width": `${leftPanelWidth}px` }}
                >
                  <div className={classNames(styles.card, styles.leftPanel)}>
                    <div className={styles.cardHeader}>
                      <div className={styles.cardTitle}>
                        {t("managementReports.reportsTitle", "All Reports")}
                      </div>
                    </div>

                    <div
                      className={classNames(styles.cardBody, styles.leftPanelBody)}
                      ref={leftSideRef}
                    >
                      {isTreeLoading && !hasTree ? (
                        <div className={styles.leftPanelLoading}>
                          <LoadingSkeleton />
                        </div>
                      ) : !hasTree ? (
                        <div className={styles.emptyPanel}>
                          <FileBarChart size={44} className={styles.emptyPanelIcon} />
                          <div className={styles.emptyPanelTitle}>
                            {t("managementReports.reportsTitle", "All Reports")}
                          </div>
                          <div className={styles.emptyPanelDescription}>
                            {t(
                              "managementReports.search.noResults",
                              "No reports found",
                            )}
                          </div>
                        </div>
                      ) : searchQuery ? (
                        filteredSearchResults?.length > 0 ? (
                          <div className={styles.results}>
                            {filteredSearchResults.map((page) => (
                              <button
                                type="button"
                                key={page.page_id}
                                onClick={() =>
                                  getConfluencePageData(page.page_id, page.page_name)
                                }
                                className={classNames(styles.searchResultItem, {
                                  [styles.active]:
                                    String(selectedPageId) === String(page.page_id),
                                })}
                              >
                                <span className={styles.searchResultIcon}>
                                  <FileText size={16} />
                                </span>
                                <span className={styles.searchResultText}>
                                  {page.page_name}
                                </span>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className={styles.noResults}>
                            {t(
                              "managementReports.search.noResults",
                              "No reports found",
                            )}
                          </div>
                        )
                      ) : (
                        <ExpandableMenu
                          treeData={confluenceTreeData?.children || []}
                          onNodeClick={(pageId, pageName) =>
                            getConfluencePageData(pageId, pageName)
                          }
                          selectedPageId={selectedPageId}
                        />
                      )}
                    </div>
                  </div>

                  <div
                    className={classNames(
                      styles.panelResizer,
                      isResizingPanels && styles.panelResizerDragging,
                    )}
                    role="separator"
                    aria-label={t(
                      "managementReports.resizePanelsLabel",
                      "Resize panels",
                    )}
                    tabIndex={0}
                    onPointerDown={handleResizerPointerDown}
                    onKeyDown={handleResizerKeyDown}
                  >
                    <div className={styles.panelResizerHandle} aria-hidden="true">
                      <GripVertical size={16} />
                    </div>
                  </div>

                  <div className={classNames(styles.card, styles.rightPanel)}>
                    <div className={styles.pageHeader}>
                      <div className={styles.pageHeaderLeft}>
                        <h2 className={styles.pageTitle}>
                          {confluencePageData?.title ||
                            t(
                              "managementReports.noPageSelected.title",
                              "No Report Selected",
                            )}
                        </h2>

                        {(lastEdited || updatedBy) && (
                          <div className={styles.pageMeta}>
                            {lastEdited && (
                              <span>
                                {t(
                                  "managementReports.lastEditedLabel",
                                  "Last edited",
                                )}{" "}
                                {lastEdited}
                              </span>
                            )}
                            {updatedBy && (
                              <span>
                                {lastEdited ? " · " : ""}
                                {String(updatedBy)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className={styles.pageHeaderRight}>
                        {confluencePageData?.version && (
                          <div className={styles.pageVersion}>
                            {t("managementReports.versionLabel", "Version")}{" "}
                            {confluencePageData.version}
                          </div>
                        )}
                        <button
                          type="button"
                          className={styles.editProcedureButton}
                          onClick={handleEditReportClick}
                          disabled={
                            !currentOrganization?.AtlassianDomain ||
                            !resolvedSpaceKey ||
                            !selectedPageId
                          }
                        >
                          <ExternalLink size={16} />
                          {t("managementReports.editButton", "Edit Report")}
                        </button>
                      </div>
                    </div>

                    <div className={styles.pageBody}>
                      {isComponentLoading && (
                        <div className={styles.loadingWrapper}>
                          <PageLoadingSkeleton />
                        </div>
                      )}

                      {!isComponentLoading && !isConfluencePageData && (
                        <div className={styles.emptyState}>
                          <div className={styles.emptyStateText}>
                            {t(
                              "managementReports.noPageSelected.description",
                              "Select a report from the list to view it.",
                            )}
                          </div>
                        </div>
                      )}

                      {!isComponentLoading && isConfluencePageData && (
                        <>
                          {selectedTreeNode?.children?.length > 0 && (
                            <div className={classNames(styles.innerCard, styles.structureCard)}>
                              <div className={classNames(styles.innerCardHeader, styles.structureCardHeader)}>
                                <span className={classNames(styles.innerCardTitle, styles.structureCardTitle)}>
                                  <span className={styles.innerCardTitleIcon} aria-hidden="true">
                                    <FileBarChart size={16} />
                                  </span>
                                  {t(
                                    "managementReports.pageStructureTitle",
                                    "Page Structure",
                                  )}
                                </span>
                              </div>
                              <div className={classNames(styles.innerCardBody, styles.structureCardBody)}>
                                <PageStructure nodes={selectedTreeNode.children} />
                              </div>
                            </div>
                          )}

                          {!isContentEmpty && (
                            <div className={styles.contentCard}>
                              <div
                                id="confluence-content"
                                className={styles.confluenceContent}
                                dangerouslySetInnerHTML={{
                                  __html: addOnClickHandlersToLinks(confluencePageData?.body),
                                }}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        <ConfluencePageDataModal
          modalConfluencePageData={modalConfluencePageData}
          addOnClickHandlersToLinks={addOnClickHandlersToLinks}
        />
      </div>
    </MainLayout>
  );
};

export default ManagementReports;
